import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Activity } from 'lucide-react';
import TriggerSubScreenLayout from './TriggerSubScreenLayout';
import SelectInput from '../../../../components/ui/SelectInput';
import { getEventsRequest } from '../../../../store/events/events.action';
import { selectEvents } from '../../../../store/events/events.selector';
import { getEventAttributesRequest } from '../../../../store/eventAttributes/eventAttributes.action';
import {
  selectEventAttributes,
  selectEventAttributesLoading,
} from '../../../../store/eventAttributes/eventAttributes.selector';
import { selectWorkspaceId } from '../../../../store/user';
import type { Condition } from '../../../../types/segments/segments.types';
import { EventAttributeData } from 'services/eventAttributesService/eventAttributesService';
import EventAttributesCondition from '../../../../components/eventAttributes/EventAttributesCondition';

interface EventConfigScreenProps {
  event?: string;
  conditions: { [key: string]: Condition[] };
  onConditionsChange: (conditions: { [key: string]: Condition[] }) => void;
  onEventChange: (event: string) => void;
  onBack: () => void;
  campaignStatus?: 'draft' | 'live' | 'paused' | 'archived';
  hideBackButton?: boolean;
}

const EventConfigScreen: React.FC<EventConfigScreenProps> = ({
  event = '',
  conditions,
  onConditionsChange,
  onEventChange,
  onBack,
  campaignStatus,
  hideBackButton = false,
}) => {
  const dispatch = useDispatch();
  const workspaceId = useSelector(selectWorkspaceId);
  const events = useSelector(selectEvents);
  const eventAttributes = useSelector(selectEventAttributes);
  const eventAttributesLoading = useSelector(selectEventAttributesLoading);

  const [selectedEvent, setSelectedEvent] = useState(event);

  useEffect(() => {
    if (workspaceId) dispatch(getEventsRequest());
  }, [workspaceId, dispatch]);

  useEffect(() => {
    if (!selectedEvent) return undefined;

    const timeoutId = window.setTimeout(() => {
      dispatch(getEventAttributesRequest(selectedEvent));
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [selectedEvent, dispatch]);

  useEffect(() => {
    setSelectedEvent((prev) =>
      event !== undefined && event !== prev ? event : prev
    );
  }, [event]);

  const eventOptions = events.map((e) => ({ value: e.event, label: e.event }));
  const attributeOptions =
    eventAttributes?.attributes
      ?.filter((a: EventAttributeData) => !a.isDeleted)
      .map((a: EventAttributeData) => ({ value: a.name, label: a.name })) ?? [];

  const handleEventChange = useCallback(
    (value: string) => {
      setSelectedEvent(value);
      onEventChange(value);
    },
    [onEventChange]
  );

  return (
    <TriggerSubScreenLayout
      onBack={onBack}
      showLiveBanner={campaignStatus === 'live'}
      hideBackButton={hideBackButton}
    >
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-chart-7/10">
            <Activity className="w-6 h-6 text-chart-7" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Event trigger</h3>
            <p className="text-sm text-muted-foreground">
              A person performs an event in your product.
            </p>
          </div>
        </div>

        <div className="max-w-md">
          <SelectInput
            label="Event"
            placeholder="Select or type an event name"
            options={eventOptions}
            value={selectedEvent}
            onChange={handleEventChange}
          />
        </div>

        {selectedEvent && (
          <div className="pt-6">
            <EventAttributesCondition
              conditions={conditions}
              onConditionsChange={onConditionsChange}
              eventAttributeOptions={attributeOptions}
              loading={eventAttributesLoading}
            />
          </div>
        )}
      </div>
    </TriggerSubScreenLayout>
  );
};

export default EventConfigScreen;
